import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { useCompare } from '@/lib/compare';
import { useCart } from '@/lib/cart';
import { api } from '@/lib/api';
import { resolveImageUrl } from '@/components/ImageUploader';
import { formatCOP } from '@/lib/format';
import { Star, X, ShoppingCart, BadgeCheck, MapPin, Truck, ShieldCheck } from 'lucide-react';
import { useSEO } from '@/lib/useSEO';
import { toast } from 'sonner';

export default function Compare() {
  const { ids, remove, clear } = useCompare();
  const { add } = useCart();
  const [products, setProducts] = useState([]);

  useSEO({
    title: 'Comparar repuestos | AutoMarket Colombia',
    description: 'Compara hasta 4 repuestos lado a lado: precio, garantía, compatibilidad, condición y stock.',
    canonical: '/comparar',
  });

  useEffect(() => {
    Promise.all(ids.map((id) => api.get(`/products/${id}`).then((r) => r.data.product).catch(() => null)))
      .then((arr) => setProducts(arr.filter(Boolean)));
  }, [ids]);

  // Highlight cheapest price across the comparison columns
  const minPrice = products.length ? Math.min(...products.map((p) => p.price)) : 0;
  const warrantyOf = (p) => {
    if (p.warranty_months) return `${p.warranty_months} mes${p.warranty_months > 1 ? 'es' : ''}`;
    if (p.warranty) return p.warranty;
    return '30 días (estándar)';
  };

  const rows = [
    { label: 'Imagen', render: (p) => p.images?.[0] && <img src={resolveImageUrl(p.images[0])} alt="" className="h-24 w-24 object-cover mx-auto" /> },
    { label: 'Marca', highlight: true, render: (p) => <span className="font-bold text-slate-900">{p.brand}</span> },
    { label: 'Precio', highlight: true, render: (p) => (
        <div className="flex flex-col items-center gap-1">
          <span className={`font-display font-black text-lg ${p.price === minPrice && products.length > 1 ? 'text-emerald-700' : 'text-slate-900'}`}>
            {formatCOP(p.price)}
          </span>
          {p.price === minPrice && products.length > 1 && (
            <span className="bg-emerald-600 text-white px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider">Mejor precio</span>
          )}
        </div>
    )},
    { label: 'Garantía', highlight: true, render: (p) => (
        <span className="inline-flex items-center gap-1 text-slate-900 font-semibold">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
          {warrantyOf(p)}
        </span>
    )},
    { label: 'Compatibilidad', highlight: true, render: (p) => (
        <span className="text-sm text-slate-700">
          {(p.vehicle_compat || []).length > 0
            ? (p.vehicle_compat || []).join(', ')
            : <span className="text-slate-400">No especificada</span>}
        </span>
    )},
    { label: 'Categoría', render: (p) => p.category },
    { label: 'Referencia', render: (p) => <span className="font-mono text-xs">{p.reference}</span> },
    { label: 'Condición', render: (p) => p.condition === 'original'
        ? <span className="bg-emerald-600 text-white px-2 py-0.5 text-[10px] font-bold uppercase">Original</span>
        : <span className="bg-slate-200 text-slate-700 px-2 py-0.5 text-[10px] font-bold uppercase">Genérico</span> },
    { label: 'Stock', render: (p) => p.stock > 0 ? <span className="text-emerald-700 font-semibold">{p.stock} unid.</span> : <span className="text-rose-700">Agotado</span> },
    { label: 'Entrega', render: (p) => <span className="inline-flex items-center gap-1"><Truck className="h-3 w-3" />{p.delivery_days || 3} días</span> },
  ];

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Herramienta</div>
            <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Comparar productos</h1>
          </div>
          {products.length > 0 && (
            <Button onClick={clear} variant="outline" className="rounded-none">Limpiar comparación</Button>
          )}
        </div>

        {products.length === 0 ? (
          <div className="mt-8 border border-slate-200 bg-slate-50 p-12 text-center">
            <p className="text-slate-600">No has agregado productos a comparar. Usa el botón &quot;Comparar&quot; en las tarjetas del catálogo (hasta 4 productos).</p>
            <Link to="/catalogo" className="mt-4 inline-block">
              <Button className="rounded-none bg-slate-900 hover:bg-slate-800">Explorar catálogo</Button>
            </Link>
          </div>
        ) : (
          <div className="mt-6 border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="bg-slate-50 text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 w-40">Característica</th>
                  {products.map((p) => (
                    <th key={p.product_id} className="border-l border-slate-200 px-4 py-3 align-top relative min-w-[200px]">
                      <button
                        onClick={() => remove(p.product_id)}
                        className="absolute top-2 right-2 h-6 w-6 flex items-center justify-center bg-slate-100 hover:bg-rose-100"
                      ><X className="h-3 w-3" /></button>
                      <div className="font-display font-bold text-slate-900 text-sm leading-tight pr-6">{p.name}</div>
                      {p.seller_name && (
                        <div className="mt-1 text-xs text-slate-600 flex items-center gap-1">
                          <BadgeCheck className="h-3 w-3 text-emerald-600" />{p.seller_name}
                        </div>
                      )}
                      {p.seller_rating != null && (
                        <div className="mt-0.5 text-xs flex items-center gap-1">
                          <Star className="h-3 w-3 fill-orange-500 text-orange-500" />
                          <span className="font-semibold">{p.seller_rating}</span>
                          <span className="text-slate-400">({p.seller_reviews_count})</span>
                        </div>
                      )}
                      {p.seller_city && <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{p.seller_city}</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row, i) => (
                  <tr key={i} className={row.highlight ? 'bg-amber-50/40' : ''} data-testid={`compare-row-${row.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    <td className={`px-4 py-3 text-xs font-bold uppercase tracking-wider ${row.highlight ? 'bg-amber-100/70 text-amber-900' : 'bg-slate-50 text-slate-500'}`}>
                      {row.label}
                    </td>
                    {products.map((p) => (
                      <td key={p.product_id} className="border-l border-slate-200 px-4 py-3 text-sm text-center">{row.render(p)}</td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className="bg-slate-50 px-4 py-3" />
                  {products.map((p) => (
                    <td key={p.product_id} className="border-l border-slate-200 px-4 py-3 text-center">
                      <Button
                        onClick={() => { add(p); toast.success('Agregado al carrito'); }}
                        disabled={p.stock === 0}
                        className="rounded-none bg-slate-900 hover:bg-slate-800 w-full"
                      >
                        <ShoppingCart className="h-4 w-4 mr-1" /> Agregar
                      </Button>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
